import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Editor, Element, Frame, useEditor, useNode } from "@craftjs/core";
import { Button, Form, Modal, Offcanvas } from "react-bootstrap";
import { Bounce, toast } from "react-toastify";
import type { DashboardWidget } from "../../types/dashboard/dashboard.types";
import WidgetRenderer from "./widget/WidgetRenderer";

type BuilderWidgetType = "fleet_counter" | "fleet_state" | "alerts" | "fleet_co2" | "stats" | "transmission_gap" | "content_html";
type CounterMetricKey =
  | "vehicles"
  | "drivers"
  | "reports"
  | "users"
  | "notifications"
  | "pois"
  | "tags"
  | "devices"
  | "alarms"
  | "tickets"
  | "tms"
  | "sites"
  | "snapshots"
  | "vehicle_groups"
  | "driver_groups"
  | "device_groups"
  | "custom";

const STORAGE_KEY = "dash2_craft_builder_state";
export const CRAFT_DASHBOARD_SAVE_EVENT = "dash2:save-craft-dashboard";
const dashboardToastOptions = { position: "bottom-right" as const, autoClose: 1800, transition: Bounce };

const BuilderUiContext = React.createContext({
  settingsSuppressed: false,
  suppressSettings: (_duration?: number) => {},
  openToolbox: (_sectionId?: string, _widgetWidth?: number) => {},
  draggingSectionId: "",
  setDraggingSectionId: (_sectionId: string) => {},
});

const defaultContentByType: Record<BuilderWidgetType, Record<string, any>> = {
  fleet_counter: { metricKey: "vehicles", numberOfItem: 0, title: "Vehicules", icon: "car", color: "bg-info-light", linkTo: "/vehicles" },
  fleet_state: { title: "Apercu de votre flotte", refreshSeconds: 60, DrivingValue: 5, ParkingValue: 10, ParkingEngineRunningValue: 2, LastTransmissionValue: 0 },
  alerts: { Notifications: [] },
  fleet_co2: { title: "Surveillance CO2", periodHours: 24, data: { emissions: [12, 18, 9, 14], categories: ["0-6h", "6-12h", "12-18h", "18-24h"], target: 45 } },
  stats: { title: "Vitesse" },
  transmission_gap: { title: "Absence transmission", thresholdHours: 1, refreshSeconds: 60 },
  content_html: {
    title: "Titre du dashboard",
    text: "Ajoute ici un texte court pour expliquer cette section.",
    html: "<p>Contenu HTML libre.</p>",
    titleLevel: "h3",
    align: "left",
  },
};

const counterMetricPresets: Record<CounterMetricKey, { title: string; icon: string; color: string; linkTo: string; numberOfItem: number }> = {
  vehicles: { title: "Vehicules", icon: "car", color: "bg-info-light", linkTo: "/vehicles", numberOfItem: 0 },
  drivers: { title: "Conducteurs", icon: "user-tie", color: "bg-danger-light", linkTo: "/drivers", numberOfItem: 0 },
  reports: { title: "Rapports", icon: "chart-bar", color: "bg-success-light", linkTo: "/reports", numberOfItem: 0 },
  users: { title: "Utilisateurs", icon: "users-cog", color: "bg-success-user", linkTo: "/users", numberOfItem: 0 },
  notifications: { title: "Notifications", icon: "bell", color: "bg-success-user", linkTo: "/notifications", numberOfItem: 0 },
  pois: { title: "POI", icon: "map-marker-alt", color: "bg-info-light", linkTo: "/pois", numberOfItem: 0 },
  tags: { title: "Tags", icon: "tags", color: "bg-danger-light", linkTo: "/Ibutton", numberOfItem: 0 },
  devices: { title: "Devices", icon: "microchip", color: "bg-success-light", linkTo: "/Devices", numberOfItem: 0 },
  alarms: { title: "Alarmes", icon: "exclamation-triangle", color: "bg-danger-light", linkTo: "/alarms", numberOfItem: 0 },
  tickets: { title: "Tickets", icon: "ticket-alt", color: "bg-info-light", linkTo: "/tickets", numberOfItem: 0 },
  tms: { title: "TMS", icon: "route", color: "bg-success-light", linkTo: "/tms", numberOfItem: 0 },
  sites: { title: "Sites", icon: "building", color: "bg-info-light", linkTo: "/sites", numberOfItem: 0 },
  snapshots: { title: "Snapshots", icon: "camera", color: "bg-success-user", linkTo: "/snapshots", numberOfItem: 0 },
  vehicle_groups: { title: "Groupes vehicules", icon: "object-group", color: "bg-info-light", linkTo: "/groupevehicles", numberOfItem: 0 },
  driver_groups: { title: "Groupes conducteurs", icon: "users", color: "bg-danger-light", linkTo: "/group-driver", numberOfItem: 0 },
  device_groups: { title: "Groupes devices", icon: "layer-group", color: "bg-success-light", linkTo: "/groupe-device", numberOfItem: 0 },
  custom: { title: "Compteur", icon: "dashboard", color: "bg-primary", linkTo: "/vehicles", numberOfItem: 42 },
};

const sectionPresets = [
  { label: "1 colonne", columns: "12" },
  { label: "2 colonnes", columns: "6,6" },
  { label: "3 colonnes", columns: "4,4,4" },
  { label: "4 colonnes", columns: "3,3,3,3" },
  { label: "5 colonnes", columns: "2.4,2.4,2.4,2.4,2.4" },
  { label: "6 colonnes", columns: "2,2,2,2,2,2" },
  { label: "70 / 30", columns: "8,4" },
  { label: "30 / 70", columns: "4,8" },
  { label: "25 / 50 / 25", columns: "3,6,3" },
  { label: "20 / 60 / 20", columns: "2,8,2" },
];

const builderWidgetTypes: Array<{ type: BuilderWidgetType; label: string; icon: string }> = [
  { type: "fleet_counter", label: "Compteur", icon: "ri-dashboard-3-line" },
  { type: "fleet_state", label: "Etat flotte", icon: "ri-pie-chart-2-line" },
  { type: "alerts", label: "Alertes", icon: "ri-notification-3-line" },
  { type: "fleet_co2", label: "CO2", icon: "ri-bar-chart-2-line" },
  { type: "stats", label: "Vitesse", icon: "ri-speed-up-line" },
  { type: "transmission_gap", label: "Transmission", icon: "ri-radar-line" },
  { type: "content_html", label: "Titre / Texte", icon: "ri-text" },
];

const dashboardTemplates: Array<{
  id: string;
  title: string;
  description: string;
  icon: string;
  rows: Array<{ columns: string; widgets: Array<{ type: BuilderWidgetType; title?: string }> }>;
}> = [
  {
    id: "analytics",
    title: "Analytique",
    description: "KPIs, etat flotte, emissions et alertes.",
    icon: "ri-line-chart-line",
    rows: [
      { columns: "4,4,4", widgets: [{ type: "fleet_counter", title: "Vehicules" }, { type: "fleet_state" }, { type: "alerts" }] },
      { columns: "8,4", widgets: [{ type: "fleet_co2" }, { type: "stats", title: "Vitesse moyenne" }] },
    ],
  },
  {
    id: "geolocation",
    title: "Geolocalisation",
    description: "Suivi flotte, activite et alertes terrain.",
    icon: "ri-map-pin-line",
    rows: [
      { columns: "8,4", widgets: [{ type: "fleet_state" }, { type: "fleet_counter", title: "Vehicules actifs" }] },
      { columns: "6,6", widgets: [{ type: "alerts" }, { type: "stats", title: "Activite" }] },
    ],
  },
  {
    id: "tmc",
    title: "TMC / Transport",
    description: "Operations, incidents, CO2 et compteurs.",
    icon: "ri-truck-line",
    rows: [
      { columns: "3,3,3,3", widgets: [{ type: "fleet_counter", title: "Missions" }, { type: "fleet_counter", title: "Vehicules" }, { type: "alerts" }, { type: "stats", title: "Retards" }] },
      { columns: "6,6", widgets: [{ type: "fleet_state" }, { type: "fleet_co2" }] },
    ],
  },
  {
    id: "maintenance",
    title: "Maintenance",
    description: "Alertes, immobilisations et suivi technique.",
    icon: "ri-tools-line",
    rows: [
      { columns: "4,4,4", widgets: [{ type: "alerts" }, { type: "fleet_counter", title: "Immobilises" }, { type: "stats", title: "Interventions" }] },
      { columns: "12", widgets: [{ type: "fleet_state" }] },
    ],
  },
  {
    id: "direction",
    title: "Direction",
    description: "Vue executive avec chiffres essentiels.",
    icon: "ri-presentation-line",
    rows: [
      { columns: "3,3,3,3", widgets: [{ type: "fleet_counter", title: "Flotte" }, { type: "fleet_counter", title: "Actifs" }, { type: "alerts" }, { type: "fleet_co2" }] },
      { columns: "7,5", widgets: [{ type: "fleet_state" }, { type: "stats", title: "Performance" }] },
    ],
  },
];

function parseColumns(columns: string) {
  const parsed = columns.split(",").map((item) => Number(item.trim())).filter((item) => item > 0);
  return parsed.length ? parsed : [12];
}

function widthToGridSpan(width: number) {
  return Math.max(1, Math.round(Number(width || 12) * 5));
}

function normalizeWidgetWidth(width: unknown) {
  return Math.max(1, Math.min(12, Number(width) || 12));
}

function formatWidthLabel(width: number) {
  return Number.isInteger(width) ? String(width) : width.toFixed(1);
}

function parseWidgetContent(content: unknown) {
  if (!content) return {};
  if (typeof content !== "string") return typeof content === "object" ? content as Record<string, any> : {};
  try {
    const parsed = JSON.parse(content);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function parseDashboardCsvNumbers(value: string) {
  return value.split(",").map((item) => Number(item.trim()) || 0);
}

function parseDashboardCsvStrings(value: string) {
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

function isDisplayConfigurableWidget(widgetType: BuilderWidgetType) {
  return widgetType === "fleet_state" || widgetType === "fleet_co2" || widgetType === "stats" || widgetType === "transmission_gap";
}

function ColumnIcon({ columns }: { columns: string }) {
  return (
    <span className="dash2-column-icon">
      {parseColumns(columns).map((column, index) => (
        <span key={`${columns}-${index}`} style={{ flex: column }} />
      ))}
    </span>
  );
}

function nodeType(node: any) {
  return typeof node?.data?.type === "string" ? node?.data?.type : node?.data?.type?.displayName || node?.data?.type?.name;
}

function toWidget(type: BuilderWidgetType, content: string, width: number): DashboardWidget {
  return { id: "craft-preview", id_widget: 0, id_dashboard: 2, id_user: 1, type, content, x: 0, y: 0, w: width, h: 4 };
}

function createCraftWidgetNode(query: any, widgetType: BuilderWidgetType, width: number, content = defaultContentByType[widgetType]) {
  const contentValue = typeof content === "string" ? content : JSON.stringify(content);
  return query
    .parseSerializedNode({
      type: { resolvedName: "CraftDashboardWidget" },
      isCanvas: false,
      props: { widgetType, content: contentValue, width },
      custom: {},
      displayName: "CraftDashboardWidget",
      hidden: false,
      nodes: [],
      linkedNodes: {},
    } as any)
    .toNode();
}

function createCraftSectionNode(query: any, sectionProps: string | Record<string, any>) {
  const props: Record<string, any> = typeof sectionProps === "string" ? { columns: sectionProps } : { ...sectionProps };
  delete props.children;

  return query
    .parseSerializedNode({
      type: { resolvedName: "CraftSection" },
      isCanvas: true,
      props,
      custom: {},
      displayName: "CraftSection",
      hidden: false,
      nodes: [],
      linkedNodes: {},
    } as any)
    .toNode();
}

function duplicateWidgetNode(query: any, actions: any, widgetId: string, parentId: string, index?: number) {
  const widgetProps = query.node(widgetId).get()?.data?.props || {};
  const widgetType = (widgetProps.widgetType || "fleet_counter") as BuilderWidgetType;
  actions.add(
    createCraftWidgetNode(query, widgetType, Number(widgetProps.width || 4), widgetProps.content || defaultContentByType[widgetType]),
    parentId,
    index
  );
}

function duplicateSectionNode(query: any, actions: any, sectionId: string, parentId: string, index?: number) {
  const sectionNode = query.node(sectionId).get();
  const newSection = createCraftSectionNode(query, sectionNode?.data?.props || { columns: "12" });
  actions.add(newSection, parentId, index);
  query.node(sectionId).childNodes().forEach((childId: string) => {
    if (nodeType(query.node(childId).get()) === "CraftDashboardWidget") {
      duplicateWidgetNode(query, actions, childId, newSection.id);
    }
  });
}

function deleteCraftNode(actions: any, nodeId: string, successMessage = "Element supprime") {
  actions.selectNode();
  window.requestAnimationFrame(() => {
    try {
      actions.setHidden(nodeId, true);
      toast.success(successMessage, dashboardToastOptions);
    } catch {
      toast.error("Erreur lors de la suppression", { ...dashboardToastOptions, autoClose: 2600 });
    }
  });
}

type CraftDashboardWidgetProps = { widgetType?: BuilderWidgetType; content?: string; width?: number };

const CraftDashboardWidget = ({
  widgetType = "fleet_counter",
  content = JSON.stringify(defaultContentByType.fleet_counter),
  width = 4,
}: CraftDashboardWidgetProps) => {
  const safeWidth = normalizeWidgetWidth(width);
  const { actions, query } = useEditor();
  const { suppressSettings } = useContext(BuilderUiContext);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const {
    connectors: { connect, drag },
    selected,
    id,
    parent,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id, parent: node.data.parent }));

  const stopWidgetAction = (event: React.SyntheticEvent) => {
    suppressSettings();
    event.stopPropagation();
  };

  const blockWidgetSurface = (event: React.SyntheticEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest(".craft-widget-action, .craft-widget-drag-handle, .modal")) return;
    suppressSettings();
    actions.selectNode();
    event.stopPropagation();
  };

  const duplicateWidget = (event: React.SyntheticEvent) => {
    stopWidgetAction(event);
    if (!parent) return;
    const siblings = query.node(parent).childNodes();
    const currentIndex = siblings.indexOf(id);
    duplicateWidgetNode(query, actions, id, parent, currentIndex + 1);
    actions.selectNode();
  };

  return (
    <article
      ref={(ref) => {
        if (ref) connect(ref);
      }}
      className={`craft-widget ${selected ? "selected" : ""}`}
      style={{ "--craft-widget-width": widthToGridSpan(safeWidth), gridColumn: `span ${widthToGridSpan(safeWidth)} / span ${widthToGridSpan(safeWidth)}` } as React.CSSProperties}
      onMouseDownCapture={blockWidgetSurface}
      onClick={blockWidgetSurface}
    >
      <div className="craft-widget-toolbar">
        <span>{widgetType}</span>
        <div className="craft-widget-actions">
          <button
            type="button"
            ref={(ref) => {
              if (ref) drag(ref);
            }}
            className="craft-action-btn craft-widget-drag-handle"
            title="Deplacer le widget"
            onPointerDown={(event) => {
              suppressSettings(1600);
              actions.selectNode();
              event.stopPropagation();
            }}
            onMouseDown={(event) => {
              suppressSettings(1600);
              actions.selectNode();
              event.stopPropagation();
            }}
            onClick={(event) => {
              suppressSettings();
              actions.selectNode();
              event.stopPropagation();
            }}
          >
            <i className="ri-drag-move-2-line" />
          </button>
          <button
            type="button"
            className="craft-action-btn craft-widget-action"
            title="Modifier le widget"
            onClick={(event) => {
              event.stopPropagation();
              actions.selectNode(id);
            }}
          >
            <i className="ri-pencil-line" />
          </button>
          <button type="button" className="craft-action-btn craft-widget-action" title="Dupliquer le widget" onClick={duplicateWidget}>
            <i className="ri-file-copy-line" />
          </button>
          <button
            type="button"
            className="craft-action-btn craft-widget-action"
            title="Supprimer le widget"
            onClick={(event) => {
              stopWidgetAction(event);
              setDeleteConfirmOpen(true);
            }}
          >
            <i className="ri-close-line" />
          </button>
        </div>
      </div>
      <div className="craft-widget-preview">
        <WidgetRenderer widget={toWidget(widgetType, content, safeWidth)} />
      </div>
      <Modal show={deleteConfirmOpen} onHide={() => setDeleteConfirmOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Supprimer le widget</Modal.Title>
        </Modal.Header>
        <Modal.Body>Voulez-vous vraiment supprimer ce widget ?</Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
          <Button
            variant="danger"
            onClick={() => {
              setDeleteConfirmOpen(false);
              deleteCraftNode(actions, id, "Widget supprime");
            }}
          >
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>
    </article>
  );
};

CraftDashboardWidget.craft = {
  displayName: "CraftDashboardWidget",
  props: { widgetType: "fleet_counter", content: JSON.stringify(defaultContentByType.fleet_counter), width: 4 },
};

type RowStretch = "default" | "stretch_row" | "stretch_row_content" | "stretch_row_content_no_spaces";
type CraftSectionProps = {
  columns?: string;
  gap?: number;
  stretch?: RowStretch;
  fullHeight?: boolean;
  columnsPlacement?: "top" | "middle" | "bottom" | "stretch";
  contentPlacement?: "default" | "top" | "middle" | "bottom";
  rtlReverse?: boolean;
  rowBackground?: string;
  rowCssClass?: string;
  rowAnchor?: string;
  videoBg?: boolean;
  videoUrl?: string;
  hideRow?: boolean;
  collapsed?: boolean;
  children?: React.ReactNode;
};

const CraftSection = ({
  columns = "12",
  gap = 16,
  stretch = "default",
  fullHeight = false,
  columnsPlacement = "top",
  contentPlacement = "default",
  rtlReverse = false,
  rowBackground = "",
  rowCssClass = "",
  rowAnchor = "",
  hideRow = false,
  collapsed = false,
  children,
}: CraftSectionProps) => {
  const { suppressSettings, openToolbox, draggingSectionId, setDraggingSectionId } = useContext(BuilderUiContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);
  const [widgetPickerWidth, setWidgetPickerWidth] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [dropPlacement, setDropPlacement] = useState<"before" | "after" | null>(null);
  const {
    connectors: { connect },
    id,
    parent,
    selected,
  } = useNode((node) => ({ id: node.id, parent: node.data.parent, selected: node.events.selected }));
  const { actions, query, visibleChildCount } = useEditor((state) => {
    const sectionNode = state.nodes[id];
    const childIds = sectionNode?.data?.nodes || [];
    return {
      visibleChildCount: childIds.filter((childId: string) => {
        const child = state.nodes[childId];
        return child && !child.data.hidden;
      }).length,
    };
  });
  const widths = parseColumns(columns);

  const stopCraftEvent = (event: React.SyntheticEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest(".craft-row-drag-handle")) return;
    suppressSettings();
    event.stopPropagation();
  };

  const closeSelectedDrawer = () => {
    suppressSettings();
    actions.selectNode();
  };

  const blockSectionSurface = (event: React.SyntheticEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest(".craft-widget, .craft-row-drag-handle, button, input, select, textarea, .modal")) return;
    actions.selectNode();
    event.stopPropagation();
  };

  const addWidgetToColumn = (width: number, widgetType: BuilderWidgetType = "fleet_counter") => {
    actions.add(createCraftWidgetNode(query, widgetType, width), id);
    setWidgetPickerWidth(null);
    closeSelectedDrawer();
  };

  const applyInlineColumns = (nextColumns: string) => {
    const nextWidths = parseColumns(nextColumns);
    actions.setProp(id, (draft: any) => {
      draft.columns = nextColumns;
    });
    query.node(id).childNodes().forEach((childId, index) => {
      if (nodeType(query.node(childId).get()) === "CraftDashboardWidget") {
        actions.setProp(childId, (draft: any) => {
          draft.width = nextWidths[index % nextWidths.length];
        });
      }
    });
    setLayoutPickerOpen(false);
    closeSelectedDrawer();
  };

  const duplicateSection = () => {
    if (!parent) return;
    const siblings = query.node(parent).childNodes();
    const currentIndex = siblings.indexOf(id);
    duplicateSectionNode(query, actions, id, parent, currentIndex + 1);
    closeSelectedDrawer();
  };

  const moveSection = (direction: -1 | 1) => {
    if (!parent) return;
    const siblings = query.node(parent).childNodes();
    const currentIndex = siblings.indexOf(id);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= siblings.length) return;
    actions.move(id, parent, nextIndex);
    setMenuOpen(false);
    closeSelectedDrawer();
  };

  const moveDraggedSectionHere = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!draggingSectionId || draggingSectionId === id || !parent) {
      setDraggingSectionId("");
      setDropPlacement(null);
      return;
    }
    const targetSiblings = query.node(parent).childNodes();
    const sourceNode = query.node(draggingSectionId).get();
    const sourceParent = sourceNode?.data?.parent;
    const sourceSiblings = sourceParent ? query.node(sourceParent).childNodes() : [];
    const sourceIndex = sourceSiblings.indexOf(draggingSectionId);
    const targetIndex = targetSiblings.indexOf(id);
    if (!sourceParent || targetIndex < 0) {
      setDraggingSectionId("");
      setDropPlacement(null);
      return;
    }
    const insertIndex = targetIndex + (dropPlacement === "after" ? 1 : 0);
    const nextIndex = sourceParent === parent && sourceIndex >= 0 && sourceIndex < insertIndex ? insertIndex - 1 : insertIndex;
    if (sourceParent === parent && sourceIndex === nextIndex) {
      setDraggingSectionId("");
      setDropPlacement(null);
      return;
    }
    actions.move(draggingSectionId, parent, nextIndex);
    setDraggingSectionId("");
    setDropPlacement(null);
    closeSelectedDrawer();
  };

  const blockGridPlusEvent = (event: React.SyntheticEvent) => {
    suppressSettings(1200);
    event.preventDefault();
    event.stopPropagation();
  };

  const openWidgetDrawerForGrid = (event: React.SyntheticEvent, width: number) => {
    blockGridPlusEvent(event);
    actions.selectNode();
    setWidgetPickerWidth(null);
    openToolbox(id, width);
  };

  const toggleCollapsed = () => {
    actions.setProp(id, (draft: any) => {
      draft.collapsed = !collapsed;
    });
    setMenuOpen(false);
    closeSelectedDrawer();
  };

  return (
    <section
      ref={(ref) => {
        if (ref) connect(ref);
      }}
      onMouseDownCapture={blockSectionSurface}
      onClick={blockSectionSurface}
      id={rowAnchor || undefined}
      className={`craft-section ${rowCssClass} stretch-${stretch} placement-${contentPlacement} columns-${columnsPlacement} ${fullHeight ? "full-height" : ""} ${rtlReverse ? "rtl-reverse" : ""} ${hideRow ? "is-hidden" : ""} ${collapsed ? "is-collapsed" : ""} ${selected ? "selected" : ""} ${draggingSectionId === id ? "is-row-dragging" : ""} ${draggingSectionId && draggingSectionId !== id && dropPlacement ? `is-row-drop-${dropPlacement}` : ""}`}
      style={rowBackground ? { background: rowBackground } : undefined}
      onDragOver={(event) => {
        if (!draggingSectionId || draggingSectionId === id) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "move";
        const rect = event.currentTarget.getBoundingClientRect();
        setDropPlacement(event.clientY < rect.top + rect.height / 2 ? "before" : "after");
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setDropPlacement(null);
        }
      }}
      onDrop={moveDraggedSectionHere}
    >
      <div
        className="craft-section-actions"
        onClick={stopCraftEvent}
        onMouseDownCapture={stopCraftEvent}
        onPointerDownCapture={stopCraftEvent}
      >
        <div className="craft-actions-left">
          <span
            role="button"
            tabIndex={0}
            draggable
            className="craft-action-btn craft-row-drag-handle"
            title="Deplacer la rangee"
            onDragStart={(event) => {
              suppressSettings(1600);
              actions.selectNode();
              setDraggingSectionId(id);
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData("text/plain", id);
              event.dataTransfer.setData("application/x-craft-section", id);
              event.stopPropagation();
            }}
            onDragEnd={(event) => {
              setDraggingSectionId("");
              setDropPlacement(null);
              event.stopPropagation();
            }}
            onPointerDown={(event) => {
              suppressSettings(1600);
              actions.selectNode();
              event.stopPropagation();
            }}
            onMouseDown={(event) => {
              suppressSettings(1600);
              actions.selectNode();
              event.stopPropagation();
            }}
            onClick={(event) => {
              suppressSettings();
              actions.selectNode();
              event.stopPropagation();
            }}
          >
            <i className="ri-fullscreen-line" />
          </span>
          <button type="button" className="craft-action-btn" title="Disposition" onClick={() => { closeSelectedDrawer(); setLayoutPickerOpen((value) => !value); }}>
            <i className="ri-layout-column-line" />
          </button>
          <button
            type="button"
            className="craft-action-btn"
            title="Ajouter un widget"
            onClick={() => {
              closeSelectedDrawer();
              openToolbox(id);
            }}
          >
            <i className="ri-add-line" />
          </button>
        </div>
        <div className="craft-actions-right">
          <button type="button" className="craft-action-btn craft-menu-btn" title="Menu rangee" onClick={() => { closeSelectedDrawer(); setMenuOpen((value) => !value); }}>
            <i className="ri-arrow-down-s-fill" />
          </button>
          <button type="button" className="craft-action-btn" title={collapsed ? "Ouvrir la rangee" : "Reduire la rangee"} onClick={toggleCollapsed}>
            <i className={collapsed ? "ri-arrow-down-s-line" : "ri-arrow-up-s-line"} />
          </button>
          <button type="button" className="craft-action-btn" title="Modifier la rangee" onClick={() => actions.selectNode(id)}>
            <i className="ri-pencil-line" />
          </button>
          <button type="button" className="craft-action-btn" title="Dupliquer la rangee" onClick={duplicateSection}>
            <i className="ri-file-copy-line" />
          </button>
          <button type="button" className="craft-action-btn" title="Supprimer" onClick={() => { closeSelectedDrawer(); setDeleteConfirmOpen(true); }}>
            <i className="ri-close-line" />
          </button>
          {menuOpen && (
            <div
              className="craft-section-menu"
              onClick={stopCraftEvent}
              onMouseDownCapture={stopCraftEvent}
              onPointerDownCapture={stopCraftEvent}
            >
              <button type="button" onClick={() => moveSection(-1)}>
                <i className="ri-arrow-up-line" />
                Monter
              </button>
              <button type="button" onClick={() => moveSection(1)}>
                <i className="ri-arrow-down-line" />
                Descendre
              </button>
              <button type="button" onClick={() => { duplicateSection(); setMenuOpen(false); }}>
                <i className="ri-file-copy-line" />
                Dupliquer
              </button>
              <button type="button" className="danger" onClick={() => { closeSelectedDrawer(); setMenuOpen(false); setDeleteConfirmOpen(true); }}>
                <i className="ri-close-line" />
                Supprimer
              </button>
            </div>
          )}
        </div>
      </div>

      {!collapsed && layoutPickerOpen && (
        <div
          className="craft-inline-layout-picker"
          onClick={stopCraftEvent}
          onMouseDownCapture={stopCraftEvent}
          onPointerDownCapture={stopCraftEvent}
        >
          {sectionPresets.map((section) => (
            <button
              type="button"
              key={section.columns}
              className={columns === section.columns ? "active" : ""}
              onClick={() => applyInlineColumns(section.columns)}
              title={section.label}
            >
              <ColumnIcon columns={section.columns} />
            </button>
          ))}
          <button type="button" className="craft-custom-layout-btn" onClick={() => setLayoutPickerOpen(false)}>
            Personnalise
          </button>
        </div>
      )}

      {!collapsed && (
        <div className="craft-section-grid" style={{ gap }}>
          {children}
          {widths.slice(visibleChildCount).map((width, index) => (
            <button
              type="button"
              key={`empty-${index}`}
              className="craft-empty-column"
              style={{ gridColumn: `span ${widthToGridSpan(width)} / span ${widthToGridSpan(width)}` }}
              onPointerDownCapture={blockGridPlusEvent}
              onMouseDownCapture={blockGridPlusEvent}
              onClick={(event) => openWidgetDrawerForGrid(event, width)}
            >
              <i className="ri-add-line" />
            </button>
          ))}
        </div>
      )}

      <Modal show={deleteConfirmOpen} onHide={() => setDeleteConfirmOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Supprimer la rangee</Modal.Title>
        </Modal.Header>
        <Modal.Body>Voulez-vous vraiment supprimer cette rangee et tous ses widgets ?</Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
          <Button
            variant="danger"
            onClick={() => {
              setDeleteConfirmOpen(false);
              deleteCraftNode(actions, id, "Rangee supprimee");
            }}
          >
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

CraftSection.craft = {
  displayName: "CraftSection",
  props: {
    columns: "12",
    gap: 16,
    stretch: "default",
    fullHeight: false,
    columnsPlacement: "top",
    contentPlacement: "default",
    rtlReverse: false,
    rowBackground: "",
    rowCssClass: "",
    rowAnchor: "",
    videoBg: false,
    videoUrl: "",
    hideRow: false,
    collapsed: false,
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
  },
};

const CraftCanvasRoot = ({ children }: { children?: React.ReactNode }) => {
  const {
    connectors: { connect },
  } = useNode();
  return (
    <div
      ref={(ref) => {
        if (ref) connect(ref);
      }}
      className="craft-canvas-root"
    >
      {children}
    </div>
  );
};

CraftCanvasRoot.craft = { displayName: "CraftCanvasRoot", rules: { canMoveIn: () => true } };

const craftResolver = {
  CraftCanvasRoot,
  CraftSection,
  CraftDashboardWidget,
};

type CraftSerializedNode = {
  type?: string | { resolvedName?: string };
  props?: Record<string, any>;
  nodes?: string[];
  linkedNodes?: Record<string, string>;
  hidden?: boolean;
};

function sanitizeCraftDashboardNodes(nodes: Record<string, CraftSerializedNode> | null) {
  if (!nodes || typeof nodes !== "object") return null;
  const cleaned: Record<string, CraftSerializedNode> = {};

  Object.entries(nodes).forEach(([nodeId, node]) => {
    if (!node || typeof node !== "object") return;
    const props = { ...(node.props || {}) };
    delete props.children;
    const linkedNodes = Object.fromEntries(
      Object.entries(node.linkedNodes || {}).filter(([, linkedId]) => typeof linkedId === "string" && !!nodes[linkedId])
    );

    cleaned[nodeId] = {
      ...node,
      props,
      nodes: Array.isArray(node.nodes) ? node.nodes.filter((childId) => typeof childId === "string" && !!nodes[childId]) : [],
      linkedNodes,
    };
  });

  return cleaned.ROOT ? cleaned : null;
}

function sanitizeCraftDashboardJson(savedJson: string) {
  if (!savedJson) return "";
  try {
    const parsed = JSON.parse(savedJson);
    const cleaned = sanitizeCraftDashboardNodes(parsed);
    return cleaned ? JSON.stringify(cleaned) : "";
  } catch {
    return "";
  }
}

function getCraftDashboardStorageKey() {
  const userId = localStorage.getItem("userid") || localStorage.getItem("id_user") || "anonymous";
  return `${STORAGE_KEY}:user:${userId}`;
}

function saveCraftDashboardJson(serialized: string, showToast = true) {
  localStorage.setItem(getCraftDashboardStorageKey(), sanitizeCraftDashboardJson(serialized));
  if (showToast) toast.success("Dashboard sauvegarde", dashboardToastOptions);
}

function craftSerializedNodeType(node?: CraftSerializedNode) {
  return typeof node?.type === "string" ? node.type : node?.type?.resolvedName;
}

function readCraftDashboardNodes(): Record<string, CraftSerializedNode> | null {
  const savedJson = localStorage.getItem(getCraftDashboardStorageKey());
  if (!savedJson) return null;
  try {
    const parsed = JSON.parse(savedJson);
    return sanitizeCraftDashboardNodes(parsed);
  } catch {
    return null;
  }
}

export function hasCraftDashboardState() {
  const nodes = readCraftDashboardNodes();
  if (!nodes) return false;
  return Object.values(nodes).some((node) => craftSerializedNodeType(node) === "CraftSection");
}

function CraftDashboardViewWidget({ nodeId, node, onConfigure }: { nodeId: string; node: CraftSerializedNode; onConfigure: (nodeId: string) => void }) {
  const props = node.props || {};
  const widgetType = (props.widgetType || "fleet_counter") as BuilderWidgetType;
  const width = normalizeWidgetWidth(props.width);
  const span = widthToGridSpan(width);
  const content = typeof props.content === "string" ? props.content : JSON.stringify(props.content || defaultContentByType[widgetType]);

  return (
    <div
      className="craft-view-widget"
      style={{ "--craft-widget-width": span, gridColumn: `span ${span} / span ${span}` } as React.CSSProperties}
      data-widget-width={formatWidthLabel(width)}
    >
      {isDisplayConfigurableWidget(widgetType) && (
        <button
          type="button"
          className="craft-view-config-btn"
          title="Configurer le widget"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onConfigure(nodeId);
          }}
        >
          <i className="ri-settings-3-line" />
        </button>
      )}
      <WidgetRenderer widget={toWidget(widgetType, content, width)} />
    </div>
  );
}

function CraftDashboardViewSection({
  node,
  nodes,
  onConfigureWidget,
}: {
  node: CraftSerializedNode;
  nodes: Record<string, CraftSerializedNode>;
  onConfigureWidget: (nodeId: string) => void;
}) {
  const props = node.props || {};
  if (node.hidden || props.hideRow) return null;

  const columns = props.columns || "12";
  const gap = Number(props.gap ?? 16);
  const childrenIds = node.nodes || [];

  return (
    <section
      className={`craft-view-section stretch-${props.stretch || "default"} placement-${props.contentPlacement || "default"} columns-${props.columnsPlacement || "top"} ${props.fullHeight ? "full-height" : ""} ${props.rtlReverse ? "rtl-reverse" : ""}`}
      style={props.rowBackground ? { background: props.rowBackground } : undefined}
    >
      <div className="craft-view-grid" style={{ gap }}>
        {childrenIds.map((childId) => {
          const child = nodes[childId];
          if (!child || child.hidden) return null;
          if (craftSerializedNodeType(child) !== "CraftDashboardWidget") return null;
          return <CraftDashboardViewWidget key={childId} nodeId={childId} node={child} onConfigure={onConfigureWidget} />;
        })}
        {childrenIds.length === 0 &&
          parseColumns(columns).map((width, index) => (
            <div key={`${columns}-${index}`} className="craft-view-empty-slot" style={{ gridColumn: `span ${widthToGridSpan(width)} / span ${widthToGridSpan(width)}` }} />
          ))}
      </div>
    </section>
  );
}

export function CraftDashboardView() {
  const [nodes, setNodes] = useState<Record<string, CraftSerializedNode> | null>(() => readCraftDashboardNodes());
  const [configNodeId, setConfigNodeId] = useState("");
  if (!nodes) return null;

  const rootNode =
    nodes.ROOT ||
    Object.values(nodes).find((node) => craftSerializedNodeType(node) === "CraftCanvasRoot");
  const sectionIds =
    rootNode?.nodes?.filter((nodeId) => craftSerializedNodeType(nodes[nodeId]) === "CraftSection") ||
    Object.entries(nodes)
      .filter(([, node]) => craftSerializedNodeType(node) === "CraftSection")
      .map(([nodeId]) => nodeId);

  if (!sectionIds.length) return null;

  const updateViewWidgetContent = (nodeId: string, content: Record<string, any>) => {
    setNodes((currentNodes) => {
      if (!currentNodes?.[nodeId]) return currentNodes;
      const nextNodes: Record<string, CraftSerializedNode> = {
        ...currentNodes,
        [nodeId]: {
          ...currentNodes[nodeId],
          props: {
            ...(currentNodes[nodeId].props || {}),
            content: JSON.stringify(content),
          },
        },
      };
      localStorage.setItem(getCraftDashboardStorageKey(), sanitizeCraftDashboardJson(JSON.stringify(nextNodes)));
      toast.success("Configuration sauvegardee", dashboardToastOptions);
      return nextNodes;
    });
  };

  return (
    <div className="craft-dashboard-view craft-dashboard-view-display">
      {sectionIds.map((sectionId) => (
        <CraftDashboardViewSection key={sectionId} node={nodes[sectionId]} nodes={nodes} onConfigureWidget={setConfigNodeId} />
      ))}
      <CraftViewWidgetSettingsDrawer
        nodeId={configNodeId}
        node={configNodeId ? nodes[configNodeId] : undefined}
        onClose={() => setConfigNodeId("")}
        onSave={(content) => {
          if (!configNodeId) return;
          updateViewWidgetContent(configNodeId, content);
          setConfigNodeId("");
        }}
      />
    </div>
  );
}

function CraftViewWidgetSettingsDrawer({
  nodeId,
  node,
  onClose,
  onSave,
}: {
  nodeId: string;
  node?: CraftSerializedNode;
  onClose: () => void;
  onSave: (content: Record<string, any>) => void;
}) {
  const props = node?.props || {};
  const widgetType = (props.widgetType || "fleet_counter") as BuilderWidgetType;
  const [draft, setDraft] = useState<Record<string, any>>({});

  useEffect(() => {
    setDraft(parseWidgetContent(props.content));
  }, [nodeId, props.content]);

  if (!nodeId || !node || craftSerializedNodeType(node) !== "CraftDashboardWidget") return null;
  if (!isDisplayConfigurableWidget(widgetType)) return null;

  const updateDraft = (key: string, value: any) => setDraft((current) => ({ ...current, [key]: value }));
  const updateDraftData = (key: string, value: any) => setDraft((current) => ({ ...current, data: { ...(current.data || {}), [key]: value } }));

  return (
    <Offcanvas show onHide={onClose} placement="end" backdrop={false} scroll className="craft-settings-drawer craft-view-settings-drawer">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Configurer widget</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {widgetType === "stats" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Titre</Form.Label>
              <Form.Control value={draft.title ?? "Vitesse"} onChange={(event) => updateDraft("title", event.target.value)} />
            </Form.Group>
            <div className="dash2-drawer-muted mb-3 mx-3">API Map temps reel, vitesse calculee depuis SOG.</div>
          </>
        )}

        {widgetType === "fleet_state" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Titre</Form.Label>
              <Form.Control value={draft.title ?? "Apercu de votre flotte"} onChange={(event) => updateDraft("title", event.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Refresh temps reel</Form.Label>
              <Form.Select value={draft.refreshSeconds ?? 60} onChange={(event) => updateDraft("refreshSeconds", Number(event.target.value))}>
                <option value={15}>15 secondes</option>
                <option value={30}>30 secondes</option>
                <option value={60}>1 minute</option>
                <option value={300}>5 minutes</option>
              </Form.Select>
            </Form.Group>
            <div className="dash2-drawer-muted mb-3 mx-3">API Map temps reel. Les valeurs ci-dessous servent de fallback.</div>
            <div className="row g-2">
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Conduite</Form.Label>
                  <Form.Control type="number" value={draft.DrivingValue ?? 0} onChange={(event) => updateDraft("DrivingValue", Number(event.target.value))} />
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Stationnement</Form.Label>
                  <Form.Control type="number" value={draft.ParkingValue ?? 0} onChange={(event) => updateDraft("ParkingValue", Number(event.target.value))} />
                </Form.Group>
              </div>
            </div>
          </>
        )}

        {widgetType === "transmission_gap" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Titre</Form.Label>
              <Form.Control value={draft.title ?? "Absence transmission"} onChange={(event) => updateDraft("title", event.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Seuil sans transmission</Form.Label>
              <Form.Select value={draft.thresholdHours ?? 1} onChange={(event) => updateDraft("thresholdHours", Number(event.target.value))}>
                <option value={1}>Plus de 1 heure</option>
                <option value={6}>Plus de 6 heures</option>
                <option value={12}>Plus de 12 heures</option>
                <option value={14}>Plus de 14 heures</option>
                <option value={24}>Plus de 24 heures</option>
                <option value={72}>Plus de 3 jours</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Refresh temps reel</Form.Label>
              <Form.Select value={draft.refreshSeconds ?? 60} onChange={(event) => updateDraft("refreshSeconds", Number(event.target.value))}>
                <option value={15}>15 secondes</option>
                <option value={30}>30 secondes</option>
                <option value={60}>1 minute</option>
                <option value={300}>5 minutes</option>
              </Form.Select>
            </Form.Group>
          </>
        )}

        {widgetType === "fleet_co2" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Titre</Form.Label>
              <Form.Control value={draft.title ?? "Surveillance CO2"} onChange={(event) => updateDraft("title", event.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Periode de calcul</Form.Label>
              <Form.Select value={draft.periodHours ?? 24} onChange={(event) => updateDraft("periodHours", Number(event.target.value))}>
                <option value={1}>1 heure</option>
                <option value={6}>6 heures</option>
                <option value={12}>12 heures</option>
                <option value={14}>14 heures</option>
                <option value={24}>24 heures</option>
                <option value={72}>3 jours</option>
                <option value={168}>7 jours</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Categories</Form.Label>
              <Form.Control value={(draft.data?.categories || []).join(", ")} onChange={(event) => updateDraftData("categories", parseDashboardCsvStrings(event.target.value))} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Emissions CO2 Kg</Form.Label>
              <Form.Control value={(draft.data?.emissions || []).join(", ")} onChange={(event) => updateDraftData("emissions", parseDashboardCsvNumbers(event.target.value))} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Objectif Kg</Form.Label>
              <Form.Control type="number" value={draft.data?.target ?? 45} onChange={(event) => updateDraftData("target", Number(event.target.value))} />
            </Form.Group>
          </>
        )}

        <div className="craft-row-footer">
          <Button variant="light" onClick={onClose}>Fermer</Button>
          <Button variant="primary" onClick={() => onSave(draft)}>Appliquer</Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

function CraftToolbox({
  show,
  onClose,
  targetSectionId,
  targetWidgetWidth,
}: {
  show: boolean;
  onClose: () => void;
  targetSectionId?: string;
  targetWidgetWidth?: number;
}) {
  const { actions, query, selectedId } = useEditor((state) => ({ selectedId: Array.from(state.events.selected)[0] }));

  const getSelectedSectionId = () => {
    if (targetSectionId) return targetSectionId;
    if (!selectedId) return "";
    if (nodeType(query.node(selectedId).get()) === "CraftSection") return selectedId;
    return query.node(selectedId).ancestors().find((ancestorId) => nodeType(query.node(ancestorId).get()) === "CraftSection") || "";
  };

  const addSection = (columns: string) => {
    const sectionNode = createCraftSectionNode(query, columns);
    actions.add(sectionNode, "ROOT");
    toast.success("Rangee ajoutee", dashboardToastOptions);
    onClose();
  };

  const addWidget = (widgetType: BuilderWidgetType) => {
    const sectionId = getSelectedSectionId();
    const width = targetWidgetWidth || (sectionId
      ? parseColumns(query.node(sectionId).get()?.data?.props?.columns || "12")[(query.node(sectionId).get()?.data?.nodes?.length || 0) % parseColumns(query.node(sectionId).get()?.data?.props?.columns || "12").length]
      : 12);
    if (sectionId) {
      actions.add(createCraftWidgetNode(query, widgetType, width), sectionId);
    } else {
      const sectionNode = createCraftSectionNode(query, "12");
      actions.add(sectionNode, "ROOT");
      actions.add(createCraftWidgetNode(query, widgetType, width), sectionNode.id);
    }
    onClose();
  };

  return (
    <Offcanvas show={show} onHide={onClose} placement="end" className="craft-toolbox-drawer">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Ajouter</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {!targetSectionId && (
          <div className="dash2-modal-section">
            <div className="dash2-modal-title">Rangees / dispositions</div>
            <div className="dash2-card-grid">
              {sectionPresets.map((section) => (
                <button
                  type="button"
                  key={section.label}
                  className="dash2-builder-card"
                  onClick={() => addSection(section.columns)}
                >
                  <ColumnIcon columns={section.columns} />
                  <strong>{section.label}</strong>
                  <small>Cliquer ou glisser</small>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="dash2-modal-section">
          <div className="dash2-modal-title">Widgets</div>
          <div className="dash2-card-grid dash2-widget-card-grid">
            {builderWidgetTypes.map((widget) => (
              <button
                type="button"
                key={widget.type}
                className="dash2-builder-card"
                onClick={() => addWidget(widget.type)}
              >
                <span className="dash2-content-icon">
                  <i className={widget.icon} />
                </span>
                <strong>{widget.label}</strong>
                <small>Cliquer ou glisser</small>
              </button>
            ))}
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

function CraftTemplatesDrawer({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { actions, query } = useEditor();

  const addTemplate = (template: (typeof dashboardTemplates)[number]) => {
    template.rows.forEach((row) => {
      const widths = parseColumns(row.columns);
      const sectionNode = createCraftSectionNode(query, row.columns);
      actions.add(sectionNode, "ROOT");
      row.widgets.forEach((widget, index) => {
        const content = { ...defaultContentByType[widget.type] };
        if (widget.title) content.title = widget.title;
        actions.add(createCraftWidgetNode(query, widget.type, widths[index % widths.length] || 12, content), sectionNode.id);
      });
    });
    onClose();
  };

  return (
    <Offcanvas show={show} onHide={onClose} placement="end" className="craft-toolbox-drawer craft-template-drawer">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Types de dashboard</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <div className="dash2-modal-section">
          <div className="dash2-modal-title">Dashboards prepares</div>
          <div className="dash2-modal-help">Choisis un type de dashboard complet, puis modifie ses rangées et widgets.</div>
          <div className="craft-template-grid">
            <button type="button" className="craft-template-card craft-template-card-custom" onClick={onClose}>
              <span className="craft-template-icon"><i className="ri-user-settings-line" /></span>
              <strong>Personnalise par utilisateur</strong>
              <small>Garder la configuration actuelle sans ajouter de template.</small>
            </button>
            {dashboardTemplates.map((template) => (
              <button type="button" key={template.id} className="craft-template-card" onClick={() => addTemplate(template)}>
                <span className="craft-template-icon"><i className={template.icon} /></span>
                <strong>{template.title}</strong>
                <small>{template.description}</small>
              </button>
            ))}
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

function CraftSettingsPanel() {
  const [activeTab, setActiveTab] = useState("general");
  const { settingsSuppressed } = useContext(BuilderUiContext);
  const { actions, query, selectedNode } = useEditor((state) => {
    const selectedId = Array.from(state.events.selected)[0];
    return { selectedNode: selectedId ? state.nodes[selectedId] : null };
  });
  const selectedId = selectedNode?.id;
  const props: any = selectedNode?.data?.props ?? {};
  const isWidget = nodeType(selectedNode) === "CraftDashboardWidget";
  const isSection = nodeType(selectedNode) === "CraftSection";
  if (settingsSuppressed || !selectedId || (!isWidget && !isSection)) return null;

  const updateProp = (key: string, value: any) => {
    actions.setProp(selectedId, (draft: any) => {
      draft[key] = value;
    });
  };

  const gapOptions = [0, 1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 35, 40];

  const applySectionColumns = (columns: string) => {
    const widths = parseColumns(columns);
    updateProp("columns", columns);
    (selectedNode?.data?.nodes || []).forEach((childId: string, index: number) => {
      if (nodeType(query.node(childId).get()) === "CraftDashboardWidget") {
        actions.setProp(childId, (draft: any) => {
          draft.width = widths[index % widths.length];
        });
      }
    });
  };

  const moveSelectedSection = (direction: -1 | 1) => {
    const parent = selectedNode?.data?.parent;
    if (!parent || !selectedId) return;
    const siblings = query.node(parent).childNodes();
    const currentIndex = siblings.indexOf(selectedId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= siblings.length) return;
    actions.move(selectedId, parent, nextIndex);
  };

  const duplicateSelectedSection = () => {
    const parent = selectedNode?.data?.parent;
    if (!parent || !selectedId) return;
    const siblings = query.node(parent).childNodes();
    const currentIndex = siblings.indexOf(selectedId);
    duplicateSectionNode(query, actions, selectedId, parent, currentIndex + 1);
  };

  const widgetContent = (() => {
    try {
      return JSON.parse(props.content || "{}");
    } catch {
      return {};
    }
  })();

  const updateWidgetContent = (key: string, value: any) => {
    updateProp("content", JSON.stringify({ ...widgetContent, [key]: value }));
  };

  const updateWidgetData = (key: string, value: any) => {
    updateProp("content", JSON.stringify({ ...widgetContent, data: { ...(widgetContent.data || {}), [key]: value } }));
  };

  const parseCsvNumbers = (value: string) => value.split(",").map((item) => Number(item.trim()) || 0);
  const parseCsvStrings = (value: string) => value.split(",").map((item) => item.trim()).filter(Boolean);

  const applyCounterMetric = (metricKey: CounterMetricKey) => {
    updateProp("content", JSON.stringify({ ...widgetContent, metricKey, ...counterMetricPresets[metricKey] }));
  };

  return (
    <Offcanvas show onHide={() => actions.selectNode()} placement="end" backdrop={false} scroll className="craft-settings-drawer">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{isWidget ? "Widget Reglages" : "Rangee Reglages"}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {isSection && (
          <>
            <div className="craft-row-tabs">
              {[
                { id: "general", label: "General" },
                { id: "style", label: "Options de style" },
                { id: "video", label: "Video" },
                { id: "background", label: "Background" },
                { id: "effect", label: "Effect" },
              ].map((tab) => (
                <button type="button" key={tab.id} className={activeTab === tab.id ? "active" : ""} onClick={() => setActiveTab(tab.id)}>
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "general" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Etirement de la rangee</Form.Label>
                  <Form.Select value={props.stretch || "default"} onChange={(event) => updateProp("stretch", event.target.value)}>
                    <option value="default">Par defaut</option>
                    <option value="stretch_row">Etirer la rangee</option>
                    <option value="stretch_row_content">Etirer la rangee et le contenu</option>
                    <option value="stretch_row_content_no_spaces">Etirer sans espaces</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Ecart entre les colonnes</Form.Label>
                  <Form.Select value={props.gap ?? 16} onChange={(event) => updateProp("gap", Number(event.target.value))}>
                    {gapOptions.map((gap) => (
                      <option key={gap} value={gap}>{gap}px</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Check className="mb-3" type="checkbox" label="Rangee en pleine hauteur ?" checked={!!props.fullHeight} onChange={(event) => updateProp("fullHeight", event.target.checked)} />
                {props.fullHeight && (
                  <Form.Group className="mb-3">
                    <Form.Label>Position des colonnes</Form.Label>
                    <Form.Select value={props.columnsPlacement || "middle"} onChange={(event) => updateProp("columnsPlacement", event.target.value)}>
                      <option value="middle">Milieu</option>
                      <option value="top">Haut</option>
                      <option value="bottom">Bas</option>
                      <option value="stretch">Etirer</option>
                    </Form.Select>
                  </Form.Group>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Position du contenu</Form.Label>
                  <Form.Select value={props.contentPlacement || "default"} onChange={(event) => updateProp("contentPlacement", event.target.value)}>
                    <option value="default">Par defaut</option>
                    <option value="top">Haut</option>
                    <option value="middle">Milieu</option>
                    <option value="bottom">Bas</option>
                  </Form.Select>
                </Form.Group>
                <Form.Check className="mb-3" type="checkbox" label="Reverse columns in RTL" checked={!!props.rtlReverse} onChange={(event) => updateProp("rtlReverse", event.target.checked)} />
              </>
            )}

            {activeTab === "style" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>ID de la rangee</Form.Label>
                  <Form.Control value={props.rowAnchor || ""} onChange={(event) => updateProp("rowAnchor", event.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Classe CSS</Form.Label>
                  <Form.Control value={props.rowCssClass || ""} onChange={(event) => updateProp("rowCssClass", event.target.value)} />
                </Form.Group>
              </>
            )}

            {activeTab === "video" && (
              <>
                <Form.Check className="mb-3" type="checkbox" label="Utiliser une video en arriere-plan ?" checked={!!props.videoBg} onChange={(event) => updateProp("videoBg", event.target.checked)} />
                <Form.Group className="mb-3">
                  <Form.Label>URL video</Form.Label>
                  <Form.Control disabled={!props.videoBg} value={props.videoUrl || ""} onChange={(event) => updateProp("videoUrl", event.target.value)} />
                </Form.Group>
              </>
            )}

            {activeTab === "background" && (
              <Form.Group className="mb-3">
                <Form.Label>Couleur de fond</Form.Label>
                <Form.Control type="color" value={props.rowBackground || "#f1f1f1"} onChange={(event) => updateProp("rowBackground", event.target.value)} />
              </Form.Group>
            )}

            {activeTab === "effect" && (
              <Form.Check className="mb-3" type="checkbox" label="Masquer cette rangee" checked={!!props.hideRow} onChange={(event) => updateProp("hideRow", event.target.checked)} />
            )}

            <div className="craft-section-drawer-actions">
              <Button variant="outline-secondary" onClick={() => moveSelectedSection(-1)}>Monter</Button>
              <Button variant="outline-secondary" onClick={() => moveSelectedSection(1)}>Descendre</Button>
              <Button variant="outline-secondary" onClick={duplicateSelectedSection}>Dupliquer</Button>
            </div>
            <div className="craft-row-footer">
              <Button variant="light" onClick={() => actions.selectNode()}>Fermer</Button>
              <Button variant="primary" onClick={() => actions.selectNode()}>Sauvegarder les modifications</Button>
            </div>
          </>
        )}
        {isWidget && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Largeur ({formatWidthLabel(Number(props.width ?? 4))}/12)</Form.Label>
              <Form.Range min={1} max={12} step={0.2} value={props.width ?? 4} onChange={(event) => updateProp("width", Number(event.target.value))} />
            </Form.Group>
            <div className="dash2-width-presets">
              {[
                { label: "1 col", value: 12 },
                { label: "1/2", value: 6 },
                { label: "1/3", value: 4 },
                { label: "1/4", value: 3 },
                { label: "1/5", value: 2.4 },
                { label: "4/6", value: 8 },
                { label: "70%", value: 8.4 },
                { label: "30%", value: 3.6 },
              ].map((preset) => (
                <button type="button" key={preset.label} className={Math.abs(Number(props.width ?? 4) - preset.value) < 0.01 ? "active" : ""} onClick={() => updateProp("width", preset.value)}>
                  {preset.label}
                </button>
              ))}
            </div>
            {props.widgetType === "fleet_counter" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Type de compteur</Form.Label>
                  <Form.Select value={widgetContent.metricKey || "custom"} onChange={(event) => applyCounterMetric(event.target.value as CounterMetricKey)}>
                    <option value="vehicles">Vehicules</option>
                    <option value="drivers">Conducteurs</option>
                    <option value="reports">Rapports</option>
                    <option value="users">Utilisateurs</option>
                    <option value="notifications">Notifications</option>
                    <option value="pois">POI</option>
                    <option value="tags">Tags</option>
                    <option value="devices">Devices</option>
                    <option value="alarms">Alarmes</option>
                    <option value="tickets">Tickets</option>
                    <option value="tms">TMS</option>
                    <option value="sites">Sites</option>
                    <option value="snapshots">Snapshots</option>
                    <option value="vehicle_groups">Groupes vehicules</option>
                    <option value="driver_groups">Groupes conducteurs</option>
                    <option value="device_groups">Groupes devices</option>
                    <option value="custom">Personnalise</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Titre</Form.Label>
                  <Form.Control value={widgetContent.title ?? ""} onChange={(event) => updateWidgetContent("title", event.target.value)} />
                </Form.Group>
                {(widgetContent.metricKey || "custom") === "custom" && (
                  <Form.Group className="mb-3">
                    <Form.Label>Valeur</Form.Label>
                    <Form.Control type="number" value={widgetContent.numberOfItem ?? 0} onChange={(event) => updateWidgetContent("numberOfItem", Number(event.target.value))} />
                  </Form.Group>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Route</Form.Label>
                  <Form.Control value={widgetContent.linkTo ?? ""} onChange={(event) => updateWidgetContent("linkTo", event.target.value)} />
                </Form.Group>
                <div className="row g-2">
                  <div className="col-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Icone</Form.Label>
                      <Form.Control value={widgetContent.icon ?? ""} onChange={(event) => updateWidgetContent("icon", event.target.value)} />
                    </Form.Group>
                  </div>
                  <div className="col-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Couleur</Form.Label>
                      <Form.Select value={widgetContent.color ?? "bg-primary"} onChange={(event) => updateWidgetContent("color", event.target.value)}>
                        <option value="bg-info-light">Info</option>
                        <option value="bg-danger-light">Danger</option>
                        <option value="bg-success-light">Success</option>
                        <option value="bg-success-user">User</option>
                        <option value="bg-primary">Primary</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>
              </>
            )}
            {props.widgetType === "stats" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Titre</Form.Label>
                  <Form.Control value={widgetContent.title ?? "Vitesse"} onChange={(event) => updateWidgetContent("title", event.target.value)} />
                </Form.Group>
                <div className="dash2-drawer-muted mb-3 mx-3">
                  Ce widget utilise l'API Map en temps reel et calcule la vitesse depuis SOG.
                </div>
              </>
            )}
            {props.widgetType === "fleet_state" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Titre</Form.Label>
                  <Form.Control value={widgetContent.title ?? "Apercu de votre flotte"} onChange={(event) => updateWidgetContent("title", event.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Refresh temps reel</Form.Label>
                  <Form.Select value={widgetContent.refreshSeconds ?? 60} onChange={(event) => updateWidgetContent("refreshSeconds", Number(event.target.value))}>
                    <option value={15}>15 secondes</option>
                    <option value={30}>30 secondes</option>
                    <option value={60}>1 minute</option>
                    <option value={300}>5 minutes</option>
                  </Form.Select>
                </Form.Group>
                <div className="dash2-drawer-muted mb-3 mx-3">
                  Ce widget utilise l'API Map en temps reel. Les valeurs ci-dessous servent de fallback.
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Conduite</Form.Label>
                      <Form.Control type="number" value={widgetContent.DrivingValue ?? 0} onChange={(event) => updateWidgetContent("DrivingValue", Number(event.target.value))} />
                    </Form.Group>
                  </div>
                  <div className="col-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Stationnement</Form.Label>
                      <Form.Control type="number" value={widgetContent.ParkingValue ?? 0} onChange={(event) => updateWidgetContent("ParkingValue", Number(event.target.value))} />
                    </Form.Group>
                  </div>
                </div>
              </>
            )}
            {props.widgetType === "fleet_co2" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Titre</Form.Label>
                  <Form.Control value={widgetContent.title ?? "Surveillance CO2"} onChange={(event) => updateWidgetContent("title", event.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Periode de calcul</Form.Label>
                  <Form.Select value={widgetContent.periodHours ?? 24} onChange={(event) => updateWidgetContent("periodHours", Number(event.target.value))}>
                    <option value={1}>1 heure</option>
                    <option value={6}>6 heures</option>
                    <option value={12}>12 heures</option>
                    <option value={14}>14 heures</option>
                    <option value={24}>24 heures</option>
                    <option value={72}>3 jours</option>
                    <option value={168}>7 jours</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Categories</Form.Label>
                  <Form.Control
                    value={(widgetContent.data?.categories || []).join(", ")}
                    placeholder="0-6h, 6-12h, 12-18h, 18-24h"
                    onChange={(event) => updateWidgetData("categories", parseCsvStrings(event.target.value))}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Emissions CO2 Kg</Form.Label>
                  <Form.Control
                    value={(widgetContent.data?.emissions || []).join(", ")}
                    placeholder="12, 18, 9, 14"
                    onChange={(event) => updateWidgetData("emissions", parseCsvNumbers(event.target.value))}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Objectif Kg</Form.Label>
                  <Form.Control type="number" value={widgetContent.data?.target ?? 45} onChange={(event) => updateWidgetData("target", Number(event.target.value))} />
                </Form.Group>
              </>
            )}
            {props.widgetType === "transmission_gap" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Titre</Form.Label>
                  <Form.Control value={widgetContent.title ?? "Absence transmission"} onChange={(event) => updateWidgetContent("title", event.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Seuil sans transmission</Form.Label>
                  <Form.Select value={widgetContent.thresholdHours ?? 1} onChange={(event) => updateWidgetContent("thresholdHours", Number(event.target.value))}>
                    <option value={1}>Plus de 1 heure</option>
                    <option value={6}>Plus de 6 heures</option>
                    <option value={12}>Plus de 12 heures</option>
                    <option value={14}>Plus de 14 heures</option>
                    <option value={24}>Plus de 24 heures</option>
                    <option value={72}>Plus de 3 jours</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Refresh temps reel</Form.Label>
                  <Form.Select value={widgetContent.refreshSeconds ?? 60} onChange={(event) => updateWidgetContent("refreshSeconds", Number(event.target.value))}>
                    <option value={15}>15 secondes</option>
                    <option value={30}>30 secondes</option>
                    <option value={60}>1 minute</option>
                    <option value={300}>5 minutes</option>
                  </Form.Select>
                </Form.Group>
              </>
            )}
            {props.widgetType === "content_html" && (
              <>
                <div className="row g-2">
                  <div className="col-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Niveau titre</Form.Label>
                      <Form.Select value={widgetContent.titleLevel || "h3"} onChange={(event) => updateWidgetContent("titleLevel", event.target.value)}>
                        <option value="h1">H1</option>
                        <option value="h2">H2</option>
                        <option value="h3">H3</option>
                        <option value="h4">H4</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div className="col-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Alignement</Form.Label>
                      <Form.Select value={widgetContent.align || "left"} onChange={(event) => updateWidgetContent("align", event.target.value)}>
                        <option value="left">Gauche</option>
                        <option value="center">Centre</option>
                        <option value="right">Droite</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Titre</Form.Label>
                  <Form.Control value={widgetContent.title ?? ""} onChange={(event) => updateWidgetContent("title", event.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Texte</Form.Label>
                  <Form.Control as="textarea" rows={4} value={widgetContent.text ?? ""} onChange={(event) => updateWidgetContent("text", event.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>HTML libre</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={8}
                    className="craft-html-textarea"
                    value={widgetContent.html ?? ""}
                    placeholder="<p>Votre contenu HTML...</p>"
                    onChange={(event) => updateWidgetContent("html", event.target.value)}
                  />
                </Form.Group>
              </>
            )}
            <div className="craft-row-footer craft-widget-footer">
              <Button variant="outline-danger" onClick={() => selectedId && deleteCraftNode(actions, selectedId, "Widget supprime")}>Supprimer le widget</Button>
              <Button variant="primary" onClick={() => { toast.success("Widget sauvegarde", dashboardToastOptions); actions.selectNode(); }}>Sauvegarder les modifications</Button>
            </div>
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}

function CraftTopbar({ onOpenToolbox, onOpenTemplates }: { onOpenToolbox: () => void; onOpenTemplates: () => void }) {
  const { query } = useEditor();
  const save = () => {
    try {
      saveCraftDashboardJson(query.serialize());
    } catch {
      toast.error("Erreur lors de la sauvegarde", { ...dashboardToastOptions, autoClose: 2600 });
    }
  };
  return (
    <>
      <div className="craft-topbar craft-topbar-pro">
        <div className="craft-topbar-title">
          <span className="craft-mode-badge">Mode edit</span>
          <strong>Dashboard Builder</strong>
          <small>Rangees, widgets, templates et apercu synchronise</small>
        </div>
        <div className="craft-topbar-actions">
          <Button variant="light" onClick={onOpenTemplates}><i className="ri-layout-masonry-line me-1" />Types dashboard</Button>
          <Button variant="light" onClick={onOpenToolbox}><i className="ri-add-line me-1" />Ajouter</Button>
          <Button variant="light" onClick={save}><i className="ri-save-line me-1" />Sauvegarder</Button>
          <Button variant="outline-light" onClick={() => { localStorage.removeItem(getCraftDashboardStorageKey()); window.location.reload(); }}>Reinitialiser</Button>
        </div>
      </div>
      <div className="craft-floating-actions">
        <button type="button" title="Types dashboard" onClick={onOpenTemplates}>
          <i className="ri-layout-masonry-line" />
        </button>
        <button type="button" title="Ajouter" onClick={onOpenToolbox}>
          <i className="ri-add-line" />
        </button>
        <button type="button" title="Sauvegarder" className="primary" onClick={save}>
          <i className="ri-save-line" />
        </button>
      </div>
    </>
  );
}

function CraftDashboardSaveBridge() {
  const { query } = useEditor();

  useEffect(() => {
    const saveFromPage = () => {
      try {
        saveCraftDashboardJson(query.serialize(), false);
      } catch {
        toast.error("Erreur lors de la sauvegarde", { ...dashboardToastOptions, autoClose: 2600 });
      }
    };

    window.addEventListener(CRAFT_DASHBOARD_SAVE_EVENT, saveFromPage);
    return () => window.removeEventListener(CRAFT_DASHBOARD_SAVE_EVENT, saveFromPage);
  }, [query]);

  return null;
}

function CraftEditorSurface() {
  const [toolboxOpen, setToolboxOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [toolboxTargetSectionId, setToolboxTargetSectionId] = useState<string | undefined>();
  const [toolboxTargetWidgetWidth, setToolboxTargetWidgetWidth] = useState<number | undefined>();
  const [settingsSuppressed, setSettingsSuppressed] = useState(false);
  const [draggingSectionId, setDraggingSectionId] = useState("");
  const suppressTimerRef = useRef<number | undefined>(undefined);
  const savedJson = useMemo(() => sanitizeCraftDashboardJson(localStorage.getItem(getCraftDashboardStorageKey()) || ""), []);
  const suppressSettings = (duration = 700) => {
    setSettingsSuppressed(true);
    window.clearTimeout(suppressTimerRef.current);
    suppressTimerRef.current = window.setTimeout(() => {
      setSettingsSuppressed(false);
    }, duration);
  };
  const openToolbox = (sectionId?: string, widgetWidth?: number) => {
    setToolboxTargetSectionId(sectionId);
    setToolboxTargetWidgetWidth(widgetWidth);
    setToolboxOpen(true);
  };
  const closeToolbox = () => {
    setToolboxOpen(false);
    setToolboxTargetSectionId(undefined);
    setToolboxTargetWidgetWidth(undefined);
  };

  return (
    <BuilderUiContext.Provider value={{ settingsSuppressed, suppressSettings, openToolbox, draggingSectionId, setDraggingSectionId }}>
      <CraftAutoSave />
      <CraftDashboardSaveBridge />
      <CraftTopbar onOpenToolbox={() => openToolbox()} onOpenTemplates={() => setTemplatesOpen(true)} />
      <div className="craft-builder-layout">
        <main className="craft-builder-canvas">
          <Frame json={savedJson || undefined}>
            <Element canvas is={CraftCanvasRoot}>
              <Element canvas is={CraftSection} columns="6,6">
                <CraftDashboardWidget widgetType="fleet_counter" content={JSON.stringify(defaultContentByType.fleet_counter)} width={6} />
                <CraftDashboardWidget widgetType="fleet_state" content={JSON.stringify(defaultContentByType.fleet_state)} width={6} />
              </Element>
            </Element>
          </Frame>
        </main>
        <CraftSettingsPanel />
      </div>
      <CraftToolbox show={toolboxOpen} onClose={closeToolbox} targetSectionId={toolboxTargetSectionId} targetWidgetWidth={toolboxTargetWidgetWidth} />
      <CraftTemplatesDrawer show={templatesOpen} onClose={() => setTemplatesOpen(false)} />
    </BuilderUiContext.Provider>
  );
}

function CraftAutoSave() {
  const saveTimerRef = useRef<number | undefined>(undefined);
  const { query, nodes } = useEditor((state) => ({ nodes: state.nodes }));

  useEffect(() => {
    window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      try {
        localStorage.setItem(getCraftDashboardStorageKey(), sanitizeCraftDashboardJson(query.serialize()));
      } catch {
        // La sauvegarde manuelle affichera le toast d'erreur si le stockage echoue.
      }
    }, 450);

    return () => window.clearTimeout(saveTimerRef.current);
  }, [nodes, query]);

  return null;
}

export default function CraftDashboardBuilder() {
  return (
    <div className="craft-dashboard-builder">
      <Editor enabled resolver={craftResolver} indicator={{ success: "#2563eb", error: "#dc3545" }}>
        <CraftEditorSurface />
      </Editor>
    </div>
  );
}
